import { useTranslate } from 'hooks/useTranslate';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { Observation, PagePermissions } from 'types';

import { useGetObservationsByReference } from 'modules/observations/hooks/useGetObservationsByReference';

import { ObservationsSectionContextType } from '../types/ObservationsSectionContextType';
import { observationsSectionInitValue } from '../constants/observationsSectionInitValue';

const ObservationsSectionContext =
  createContext<ObservationsSectionContextType>(observationsSectionInitValue);

export const ObservationsSectionProvider: FC<{
  children: ReactNode;
  referenceId: string;
  referenceType: string;
  permissions: PagePermissions;
}> = ({ children, referenceId, referenceType, permissions }) => {
  const { t } = useTranslate();

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [refetchKey, setRefetchKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { data: observationsData, isLoading: isLoadingObservations } =
    useGetObservationsByReference({
      referenceId,
      referenceType,
      page,
      rowsPerPage,
      refetchKey,
    });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const invalidateObservations = () => {
    setRefetchKey((v) => v + 1);
  };

  const handleAddObservation = () => {};

  const handleEditObservation = (observation: Observation) => {};

  const handleDeleteObservation = async (observationId: string) => {};

  return (
    <ObservationsSectionContext.Provider
      value={{
        data: {
          observations: observationsData?.observations || [],
          observationsTotal: observationsData?.observationsTotal || 0,
          page,
          rowsPerPage,
          isAdmin: permissions?.isAdmin || false,
          isReadOnly: permissions?.isReadOnly || false,
          isLoadingObservations,
          isLoading,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleAddObservation,
          handleEditObservation,
          handleDeleteObservation,
        },
      }}
    >
      {children}
    </ObservationsSectionContext.Provider>
  );
};

export const useObservationsSection = () => {
  const context = useContext(ObservationsSectionContext);
  if (!context) {
    throw new Error(
      'useObservationsSection must be used within a ObservationsSectionProvider'
    );
  }
  return context;
};
